import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Building2, 
  Monitor, 
  Activity, 
  Calendar,
  Clock,
  ShieldCheck,
  TrendingUp
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { toast } from 'sonner';
import API from '../api_config';

export default function SuperAdminActivityPage() {
  const [period, setPeriod] = useState('day');
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, activityRes, liveRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats?period=${period}`),
        axios.get(`${API}/super-admin/activity?period=${period}`),
        axios.get(`${API}/super-admin/live-status`)
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setLiveUsers(liveRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de monitoreo');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every minute for live status
    const interval = setInterval(() => {
        axios.get(`${API}/super-admin/live-status`)
            .then(res => setLiveUsers(res.data))
            .catch(err => console.error('Live status error:', err));
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="space-y-8 p-1">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
            <ShieldCheck className="h-10 w-10 text-primary" />
            Monitoreo Global de Actividad
          </h1>
          <p className="text-muted-foreground mt-2">Visibilidad completa de conexiones y uso de la plataforma</p>
        </div>
        
        <div className="flex bg-muted/30 p-1 rounded-xl border border-border w-fit">
          <Button 
            variant={period === 'day' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setPeriod('day')}
            className="rounded-lg px-6"
          >
            Hoy
          </Button>
          <Button 
            variant={period === 'month' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setPeriod('month')}
            className="rounded-lg px-6"
          >
            Este Mes
          </Button>
          <Button 
            variant={period === 'year' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setPeriod('year')}
            className="rounded-lg px-6"
          >
            Este Año
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Sesiones Totales" 
          value={stats?.total_sessions || 0} 
          icon={<Monitor className="h-5 w-5" />} 
          color="blue"
          loading={loading}
        />
        <StatCard 
          title="Usuarios Únicos" 
          value={stats?.active_users || 0} 
          icon={<Users className="h-5 w-5" />} 
          color="purple"
          loading={loading}
        />
        <StatCard 
          title="Empresas Activas" 
          value={stats?.active_companies || 0} 
          icon={<Building2 className="h-5 w-5" />} 
          color="orange"
          loading={loading}
        />
        <StatCard 
          title="Top Empresa" 
          value={stats?.top_company || 'N/A'} 
          icon={<TrendingUp className="h-5 w-5" />} 
          color="green"
          isText
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Trend Chart */}
        <Card className="lg:col-span-2 border-border/60 shadow-sm overflow-hidden">
          <CardHeader className="border-b bg-muted/5 py-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Tendencia de Conexiones
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-8 pb-4">
            <div className="h-[300px] w-full">
              {loading ? (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground italic">
                  Generando gráfico...
                </div>
              ) : stats?.chart_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{fill: '#64748b', fontSize: 12}}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{fill: '#64748b', fontSize: 12}}
                    />
                    <Tooltip 
                      contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={3} 
                      dot={{r: 6, fill: 'hsl(var(--primary))', strokeWidth: 2, stroke: '#fff'}}
                      activeDot={{r: 8, strokeWidth: 0}}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground opacity-50">
                  Sin datos para este periodo
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Live Status */}
        <Card className="lg:col-span-1 border-border/60 shadow-sm">
          <CardHeader className="border-b bg-muted/5 py-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              Usuarios Online Ahora
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[385px] overflow-y-auto divide-y divide-border">
              {liveUsers.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground italic">
                  No hay usuarios activos
                </div>
              ) : (
                liveUsers.map((user) => (
                  <div key={user.id} className="p-4 flex items-center gap-3 hover:bg-muted/5 transition-colors">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                        {user.full_name?.charAt(0) || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{user.full_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate uppercase font-medium">{user.company_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                           Visto: {new Date(user.last_activity).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connection Logs Table */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="border-b bg-muted/5 py-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            Registro de Conexiones
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] font-bold">
                <tr>
                  <th className="p-4 text-left">Empresa</th>
                  <th className="p-4 text-left">Usuario</th>
                  <th className="p-4 text-left">Evento</th>
                  <th className="p-4 text-right">Fecha / Hora</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="p-12 text-center text-muted-foreground italic">
                        Cargando registros...
                    </td>
                  </tr>
                ) : activity.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-12 text-center text-muted-foreground opacity-50 italic">
                        No se encontraron registros de actividad
                    </td>
                  </tr>
                ) : (
                  activity.map((log) => (
                    <tr key={log.id} className="hover:bg-muted/5 transition-colors">
                      <td className="p-4 font-semibold text-foreground uppercase tracking-tight">
                        {log.company_name}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                            <span className="font-medium">{log.full_name}</span>
                            <span className="text-[11px] text-muted-foreground font-mono">{log.email}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-1 rounded-full font-bold uppercase">
                          {log.type}
                        </span>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <div className="flex flex-col items-end">
                            <div className="flex items-center gap-1.5 text-foreground font-medium">
                                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                {new Date(log.timestamp).toLocaleDateString('es-CL')}
                            </div>
                            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mt-0.5">
                                <Clock className="h-3 w-3" />
                                {new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ title, value, icon, color, isText = false, loading = false }) {
    const colors = {
        blue: 'bg-blue-600',
        purple: 'bg-indigo-600',
        orange: 'bg-amber-600',
        green: 'bg-emerald-600'
    };

    return (
        <Card className="border-border/60 shadow-sm relative overflow-hidden group">
            <div className={`absolute top-0 right-0 w-24 h-24 -mt-8 -mr-8 rounded-full opacity-5 group-hover:scale-150 transition-transform duration-500 ${colors[color]}`} />
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className={`p-2.5 rounded-xl text-white ${colors[color]} shadow-lg shadow-${color}-200/50`}>
                        {icon}
                    </div>
                </div>
                <div className="space-y-1">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</p>
                    {loading ? (
                        <div className="h-8 w-24 bg-muted animate-pulse rounded mt-2" />
                    ) : (
                        <h3 className={`text-2xl font-bold tracking-tight truncate ${isText ? 'text-lg' : 'text-3xl font-black'}`}>
                            {value}
                        </h3>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
